import { type FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useAbility } from "@/partner/shared/lib";
import {
  useCurrentPlatformQuery,
  useUpdateCurrentPlatformMutation,
} from "@/partner/entities/platforms";
import {
  fromSelectValue,
  NO_LIMIT_OPTION,
  normalizeParallelLimit,
  PARALLEL_LIMIT_OPTIONS,
  type ParallelLimitValue,
  toSelectValue,
} from "../model/parallelLimit";

/**
 * Блок «Просмотр» страницы настроек платформы (PARTNER-PAGES.md,
 * архетип B): лимит параллельных просмотров платного контента, 1..3
 * устройства или «Без лимита» (null). Сохраняет
 * только своё поле через updateCurrentPlatform, по образцу
 * LiveReactionsSettingsCard. «Сохранить» недоступна без права `update` на
 * `Platform`.
 */
export const ViewingSettingsCard: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const ability = useAbility();
  const canUpdate = ability.can("update", "Platform");
  const { data } = useCurrentPlatformQuery();
  const platform = data?.currentPlatform;
  // Ленивый инициализатор: при платформе в кеше Apollo первый рендер сразу
  // показывает сохранённое значение.
  const [limit, setLimit] = useState<ParallelLimitValue>(() =>
    normalizeParallelLimit(platform?.parallelPremiumViewLimit)
  );

  useEffect(() => {
    if (!platform) return;
    setLimit(normalizeParallelLimit(platform.parallelPremiumViewLimit));
  }, [platform]);

  const [updateCurrentPlatform, { loading }] = useUpdateCurrentPlatformMutation(
    {
      refetchQueries: ["CurrentPlatform"],
      onCompleted: () =>
        enqueueSnackbar(t("viewingSettings.saved"), { variant: "success" }),
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    }
  );

  const isDirty =
    !!platform &&
    limit !== normalizeParallelLimit(platform.parallelPremiumViewLimit);

  const handleSave = () => {
    if (!canUpdate) return;
    void updateCurrentPlatform({
      variables: { input: { parallelPremiumViewLimit: limit } },
    });
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={t("viewingSettings.title")}
        subheader={t("viewingSettings.subtitle")}
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            label={t("viewingSettings.parallelLimitLabel")}
            helperText={t("viewingSettings.parallelLimitHelper")}
            value={toSelectValue(limit)}
            onChange={(event) => setLimit(fromSelectValue(event.target.value))}
            disabled={!canUpdate}
            sx={{ maxWidth: { sm: 360 } }}
          >
            {PARALLEL_LIMIT_OPTIONS.map((option) => (
              <MenuItem key={option} value={String(option)}>
                {option}
              </MenuItem>
            ))}
            <MenuItem value={NO_LIMIT_OPTION}>
              {t("viewingSettings.noLimit")}
            </MenuItem>
          </TextField>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || !isDirty || !canUpdate}
            >
              {t("common.actions.save")}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
